
import { collection, getDocs, addDoc, setDoc, updateDoc, deleteDoc, doc, onSnapshot, Unsubscribe } from "firebase/firestore";
import { db } from "../firebase";
import { Wine, FIRESTORE_FIELD_MAP } from '../types';
import { migrateLegacyFields } from '../utils/grapeUtils';
import { convertToHome, getCurrencySymbol } from '@/lib/currencyConversion';
// Fixed: Import CONFIG from constants instead of types
import { CONFIG } from '../constants';
import { requireUid } from '@/utils/authHelpers';
import { captureError } from '@/utils/captureError';

// Long enough that a slow-but-working connection still acks in time, short
// enough that an offline commit doesn't feel hung.
const SERVER_ACK_TIMEOUT_MS = 6000;

function userWinesCollection() {
  return collection(db, 'users', requireUid(), 'wines');
}

function userWineDoc(docId: string) {
  return doc(db, 'users', requireUid(), 'wines', docId);
}

export function docToWine(docId: string, data: Record<string, any>): Wine {
  const wine: any = { id: docId };
  Object.keys(FIRESTORE_FIELD_MAP).forEach(key => {
    const firestoreKey = FIRESTORE_FIELD_MAP[key];
    wine[key] = data[firestoreKey];
  });
  // Fallback: ensure producer is always set (imported wines may only have name)
  if (!wine.producer && wine.name) wine.producer = wine.name;
  if (wine.producer && !wine.name) wine.name = wine.producer;
  // Fallback: legacy docs may have 'Cépage' / 'Blend %' instead of 'Grape Varieties'
  if (!wine.grapeVarieties || !Array.isArray(wine.grapeVarieties) || wine.grapeVarieties.length === 0) {
    wine.grapeVarieties = migrateLegacyFields(data['Cépage'], data['Blend %']);
  }
  return wine as Wine;
}

export function wineToDoc(wine: Partial<Wine>): Record<string, any> {
  const doc: Record<string, any> = {};
  Object.keys(FIRESTORE_FIELD_MAP).forEach(key => {
    const firestoreKey = FIRESTORE_FIELD_MAP[key];
    if ((wine as any)[key] !== undefined) doc[firestoreKey] = (wine as any)[key];
  });
  return doc;
}

export const inventoryService = {
  getInventory: async (): Promise<Wine[]> => {
    const winesRef = userWinesCollection();
    const snapshot = await getDocs(winesRef);
    return snapshot.docs.map(d => docToWine(d.id, d.data())).filter(w => !!(w.producer || w.name));
  },

  onInventoryChange: (callback: (wines: Wine[]) => void): Unsubscribe => {
    const wineMap = new Map<string, Wine>();
    return onSnapshot(userWinesCollection(), (snap) => {
      for (const change of snap.docChanges()) {
        if (change.type === 'removed') {
          wineMap.delete(change.doc.id);
        } else {
          // 'added' or 'modified'
          const wine = docToWine(change.doc.id, change.doc.data());
          if (wine.producer || wine.name) {
            wineMap.set(change.doc.id, wine);
          } else {
            wineMap.delete(change.doc.id);
          }
        }
      }
      callback(Array.from(wineMap.values()));
    });
  },

  addWine: async (wine: Omit<Wine, 'id'>): Promise<string | null> => {
    try {
      // doc() + setDoc() rather than addDoc(): this yields the id
      // synchronously, which is what makes the timeout below safe.
      const ref = doc(userWinesCollection());
      const write = setDoc(ref, wineToDoc(wine));

      // Under the persistent cache a write is applied locally at once — the
      // snapshot listener fires and the wine appears — but the promise settles
      // only on server acknowledgement, which never arrives offline. Awaiting
      // it outright left the commit spinner running forever in precisely the
      // place this app gets used: a cellar, underground, with no signal.
      //
      // So wait only long enough to report a genuine failure (permission
      // denied and friends reject quickly), then hand back the id regardless.
      // The write stays in flight and syncs when the connection returns.
      const outcome = await Promise.race([
        write.then(() => 'acked' as const),
        new Promise<'pending'>((resolve) =>
          setTimeout(() => resolve('pending'), SERVER_ACK_TIMEOUT_MS)),
      ]);

      if (outcome === 'pending') {
        void write.catch((e) =>
          captureError(e, { context: 'inventory_crud', action: 'add_deferred' }));
      }

      return ref.id;
    } catch (e) {
      captureError(e, { context: 'inventory_crud', action: 'add' });
      return null;
    }
  },

  deleteWine: async (docId: string): Promise<boolean> => {
    try {
      await deleteDoc(userWineDoc(docId));
      return true;
    } catch (e) {
      captureError(e, { context: 'inventory_crud', action: 'delete' });
      return false;
    }
  },

  updateField: async (docId: string, field: string, value: any): Promise<boolean> => {
    const firestoreKey = FIRESTORE_FIELD_MAP[field] || field;
    try {
      await updateDoc(userWineDoc(docId), { [firestoreKey]: value });
      return true;
    } catch (e) {
      captureError(e, { context: 'inventory_crud', action: 'update' });
      return false;
    }
  },

  updateFields: async (docId: string, fields: Record<string, any>): Promise<boolean> => {
    const mapped: Record<string, any> = {};
    for (const [key, value] of Object.entries(fields)) {
      mapped[FIRESTORE_FIELD_MAP[key] || key] = value;
    }
    try {
      await updateDoc(userWineDoc(docId), mapped);
      return true;
    } catch (e) {
      captureError(e, { context: 'inventory_crud', action: 'batchUpdate' });
      return false;
    }
  },

  buildCellarSummary: (
    inventory: Wine[],
    homeCurrency: string = 'AUD',
    rates: Record<string, number> = {},
  ): string => {
    if (inventory.length === 0) return "Cellar is empty.";
    const sym = getCurrencySymbol(homeCurrency);

    const totalBottles = inventory.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0);

    // Type breakdown
    const types: Record<string, number> = {};
    inventory.forEach(w => { types[w.type] = (types[w.type] || 0) + (Number(w.quantity) || 0); });
    const typeStr = Object.entries(types).map(([t, n]) => `${t}: ${n}`).join(', ');

    // Top 5 helpers
    const top5 = (acc: Record<string, number>) =>
      Object.entries(acc).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, v]) => `${k} (${v})`).join(', ');

    const countries: Record<string, number> = {};
    const regions: Record<string, number> = {};
    const producers: Record<string, number> = {};
    inventory.forEach(w => {
      const qty = Number(w.quantity) || 0;
      if (w.country) countries[w.country] = (countries[w.country] || 0) + qty;
      if (w.region) regions[w.region] = (regions[w.region] || 0) + qty;
      if (w.producer) producers[w.producer] = (producers[w.producer] || 0) + qty;
    });

    // Price range (home-converted)
    const homePrices = inventory
      .map(w => convertToHome(Number(w.price) || 0, w.priceCurrency, homeCurrency, rates))
      .filter(p => p > 0);
    const priceRange = homePrices.length > 0
      ? `${sym}${Math.min(...homePrices)}-${sym}${Math.max(...homePrices)}`
      : 'N/A';

    // Vintage range
    const vintages = inventory.map(w => Number(w.vintage) || 0).filter(v => v > 0);
    const vintageRange = vintages.length > 0 ? `${Math.min(...vintages)}-${Math.max(...vintages)}` : 'N/A';

    // Maturity breakdown
    const maturity: Record<string, number> = { 'Drink Now': 0, Hold: 0, 'Past Peak': 0, Unknown: 0 };
    inventory.forEach(w => {
      const m = w.maturity || 'Unknown';
      maturity[m] = (maturity[m] || 0) + (Number(w.quantity) || 0);
    });
    const maturityStr = Object.entries(maturity).filter(([, n]) => n > 0).map(([k, v]) => `${k}: ${v}`).join(', ');

    // Location breakdown
    const locations: Record<string, number> = {};
    inventory.forEach(w => {
      if (w.location) {
        const qty = Number(w.quantity) || 0;
        locations[w.location] = (locations[w.location] || 0) + qty;
      }
    });
    const locationStr = Object.keys(locations).length > 0
      ? `Locations: ${Object.entries(locations).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} (${v})`).join(', ')}.`
      : '';

    // 3 most recent (by array order, newest last)
    const recent = inventory.slice(-3).reverse().map(w => `${w.vintage} ${w.producer}${w.name ? ' ' + w.name : ''}`).join('; ');

    const lines = [
      `${totalBottles} bottles. Home currency: ${homeCurrency}.`,
      `Types: ${typeStr}.`,
      `Countries: ${top5(countries)}.`,
      `Regions: ${top5(regions)}.`,
      `Producers: ${top5(producers)}.`,
      `Prices (converted to ${homeCurrency}): ${priceRange}. Vintages: ${vintageRange}.`,
      `Maturity: ${maturityStr}.`,
    ];
    if (locationStr) lines.push(locationStr);
    lines.push(`Recent: ${recent}.`);
    return lines.join(' ');
  },

  getCellarSnapshot: (
    inventory: Wine[],
    options?: { includeIds?: boolean },
    homeCurrency: string = 'AUD',
    rates: Record<string, number> = {},
  ): string => {
    if (inventory.length === 0) return 'Cellar is empty.';
    // Prioritize recent + highly rated wines for Remy's context window
    const scored = [...inventory].map((w, i) => ({
      wine: w,
      score: (i / inventory.length) * 50 + ((Number(w.criticRating) || 0) / 100) * 50,
    }));
    scored.sort((a, b) => b.score - a.score);
    const limited = scored.slice(0, CONFIG.INVENTORY_LIMIT).map(s => s.wine);
    let context = `Showing ${limited.length} of ${inventory.length} bottles (home currency: ${homeCurrency}):\n`;
    limited.forEach(w => {
      const id = options?.includeIds ? `[ID:${w.id}] ` : '';
      const rawPrice = Number(w.price) || 0;
      const wineCcy = w.priceCurrency || homeCurrency;
      const sym = getCurrencySymbol(wineCcy);
      let priceStr = `${sym}${rawPrice}`;
      if (wineCcy !== homeCurrency && rawPrice > 0) {
        const homeVal = convertToHome(rawPrice, wineCcy, homeCurrency, rates);
        priceStr += ` (~${getCurrencySymbol(homeCurrency)}${Math.round(homeVal)})`;
      }
      const locStr = w.location ? `, Location: ${w.location}` : '';
      context += `- ${id}${w.vintage} ${w.producer} ${w.name} (${w.type}, ${priceStr}, Qty: ${w.quantity}, Maturity: ${w.maturity}${locStr})\n`;
    });
    return context;
  },

  localQuery: (inventory: Wine[], filters: any): Wine[] => {
    return inventory.filter(w => {
      if (filters.type && w.type !== filters.type) return false;
      if (filters.producer && !w.producer.toLowerCase().includes(filters.producer.toLowerCase())) return false;
      return true;
    }).slice(0, 5);
  }
};
